import "./TourTray.css";

import { Controller } from "@hotwired/stimulus";
import { HorizontalRailScroller } from "../HorizontalRail/HorizontalRailDrag";
import { formatTranslation, webTranslations } from "../webTranslations";
import { tourPoiSelectionChangedEventName, type TourPoiSelectionSnapshot } from "./TourPoiSelectionEvents";
import { getTourPoiSelectionState } from "./TourPoiSelectionState";
import { TourTrayDrag } from "./TourTrayDrag";

type TourTrayPoi = {
    id: string;
    name: string;
    imageUrl?: string | null;
    order: number;
    canonicalUrl: string;
    isSelected?: boolean;
    isSubscriberOnly?: boolean;
};

type TourTrayContext = {
    tourContextKey: string;
    tourId: string;
    tourName: string;
    tourUrl: string;
    tourType?: string;
    visitedCount: number;
    items: TourTrayPoi[];
    expandedOnLoad?: boolean;
};

type TourTrayState = "collapsed" | "compact" | "full";

export default class TourTrayController extends Controller<HTMLElement> {
    static targets = ["title", "items", "toggleButton", "expandButton", "collapseButton", "body", "tooltip"];

    declare readonly titleTarget: HTMLElement;
    declare readonly toggleButtonTarget: HTMLButtonElement;
    declare readonly itemsTarget: HTMLElement;
    declare readonly expandButtonTarget: HTMLButtonElement;
    declare readonly collapseButtonTarget: HTMLButtonElement;
    declare readonly bodyTarget: HTMLElement;
    declare readonly tooltipTarget: HTMLElement;

    private currentContext?: TourTrayContext;
    private trayState: TourTrayState = "compact";
    private compactTapExpands = false;
    private trayDrag?: TourTrayDrag;
    private dragHeights?: { start: number; min: number; max: number };
    private horizontalRailScroller?: HorizontalRailScroller;
    private resizeObserver?: ResizeObserver;
    private selectedItemScrollAnimationFrame?: number;
    private scrollAnimationFrame?: number;
    private scrollTarget?: number;
    private resizeAnimationFrame?: number;
    private resizeCompletionTimer?: ReturnType<typeof setTimeout>;
    private tooltipAnchor?: HTMLElement;
    private resizeWantsAlignment = false;

    connect(): void {
        this.trayDrag = new TourTrayDrag(this.element, {
            start: this.startTrayDrag, move: this.previewTrayDrag, end: this.finishTrayDrag
        });
        this.trayDrag.connect();
        this.toggleButtonTarget.addEventListener("click", this.toggle);
        this.horizontalRailScroller = new HorizontalRailScroller({
            root: this.element,
            railSelector: ".smartguide-tour-tray__body",
            scrollLeftDataAttribute: "tourTrayScrollLeft"
        });
        this.horizontalRailScroller.connect();
        this.connectHeightTracking();
        this.refreshContext();
        document.addEventListener("turbo:load", this.onTurboLoad);
        document.addEventListener("turbo:render", this.onTurboRender);
        document.addEventListener("turbo:before-cache", this.onBeforeCache);
        document.addEventListener(tourPoiSelectionChangedEventName, this.onTourPoiSelectionChanged);
        this.itemsTarget.addEventListener("click", this.onItemClick);
        this.bodyTarget.addEventListener("pointerdown", this.cancelSelectedItemScroll);
        this.bodyTarget.addEventListener("wheel", this.cancelSelectedItemScroll, { passive: true });
        this.itemsTarget.addEventListener("mouseover", this.onItemTooltip);
        this.itemsTarget.addEventListener("focusin", this.onItemTooltip);
        this.itemsTarget.addEventListener("mouseout", this.onTooltipLeave);
        this.itemsTarget.addEventListener("focusout", this.onTooltipLeave);
        document.addEventListener("keydown", this.onKeyDown);
        this.element.addEventListener("transitionend", this.onResizeTransitionEnd);
        window.addEventListener("pageshow", this.onPageShow);
    }

    disconnect(): void {
        this.trayDrag?.disconnect();
        this.trayDrag = undefined;
        this.toggleButtonTarget.removeEventListener("click", this.toggle);
        this.horizontalRailScroller?.disconnect();
        this.horizontalRailScroller = undefined;
        document.removeEventListener("turbo:load", this.onTurboLoad);
        document.removeEventListener("turbo:render", this.onTurboRender);
        document.removeEventListener("turbo:before-cache", this.onBeforeCache);
        document.removeEventListener(tourPoiSelectionChangedEventName, this.onTourPoiSelectionChanged);
        this.itemsTarget.removeEventListener("click", this.onItemClick);
        this.bodyTarget.removeEventListener("pointerdown", this.cancelSelectedItemScroll);
        this.bodyTarget.removeEventListener("wheel", this.cancelSelectedItemScroll);
        this.itemsTarget.removeEventListener("mouseover", this.onItemTooltip);
        this.itemsTarget.removeEventListener("focusin", this.onItemTooltip);
        this.itemsTarget.removeEventListener("mouseout", this.onTooltipLeave);
        this.itemsTarget.removeEventListener("focusout", this.onTooltipLeave);
        document.removeEventListener("keydown", this.onKeyDown);
        this.element.removeEventListener("transitionend", this.onResizeTransitionEnd);
        window.removeEventListener("pageshow", this.onPageShow);
        this.cancelSelectedItemScroll();
        this.cancelTrayResize();
        this.hideTooltip();
        this.disconnectHeightTracking();
        this.clearTrayHeightVariable();
    }

    expand(event?: Event): void {
        event?.preventDefault();
        this.setTrayState(this.trayState === "collapsed" ? "compact" : "full", true);
    }

    collapse(event?: Event): void {
        event?.preventDefault();
        this.setTrayState(this.trayState === "full" ? "compact" : "collapsed", true);
    }

    private readonly toggle = (): void => {
        if (this.trayState === "collapsed" || (this.trayState === "compact" && this.compactTapExpands)) this.expand();
        else this.collapse();
    };

    private adjacentState(up: boolean): TourTrayState {
        if (up) return this.trayState === "collapsed" ? "compact" : "full";
        return this.trayState === "full" ? "compact" : "collapsed";
    }

    private applyTrayAppearance(state: TourTrayState): void {
        this.element.dataset.tourTrayState = state;
        this.element.classList.toggle("smartguide-tour-tray--expanded", state !== "collapsed");
        this.bodyTarget.hidden = state === "collapsed";
        this.itemsTarget.querySelectorAll<HTMLAnchorElement>("[data-tour-tray-poi-card]").forEach(link => this.placeItemMarker(link, state));
    }

    private readonly startTrayDrag = (): void => {
        this.cancelSelectedItemScroll();
        this.horizontalRailScroller?.stopMomentum();
        this.cancelTrayResize();
        const start = this.element.getBoundingClientRect().height;
        const scrollLeft = this.bodyTarget.scrollLeft;
        this.applyTrayAppearance(this.adjacentState(false));
        const min = this.element.getBoundingClientRect().height;
        this.applyTrayAppearance(this.adjacentState(true));
        const max = this.element.getBoundingClientRect().height;
        this.applyTrayAppearance(this.trayState);
        this.bodyTarget.scrollLeft = scrollLeft;
        this.dragHeights = { start, min, max };
        this.element.classList.add("smartguide-tour-tray--dragging");
    };

    private readonly previewTrayDrag = (delta: number): void => {
        const heights = this.dragHeights;
        if (!heights) return;
        this.element.style.height = `${Math.max(heights.min, Math.min(heights.max, heights.start - delta))}px`;
        this.applyTrayAppearance(delta < 0 ? this.adjacentState(true) : this.trayState);
        this.updateTrayHeightVariable();
    };

    private readonly finishTrayDrag = (delta: number | undefined): void => {
        if (!this.dragHeights) return;
        const height = this.element.getBoundingClientRect().height;
        this.dragHeights = undefined;
        this.element.classList.remove("smartguide-tour-tray--dragging");
        this.cancelTrayResize();
        const next = delta !== undefined && Math.abs(delta) >= 24 ? this.adjacentState(delta < 0) : this.trayState;
        this.setTrayState(next, true, height);
    };

    showTooltip(event: Event): void {
        const target = event.target instanceof Element ? event.target : null;
        const anchor = target?.closest<HTMLElement>("[data-tour-tray-poi-card]");
        if (!anchor || !this.element.contains(anchor) || anchor.hidden) return;
        if (this.trayState !== "compact") return;
        const label = anchor.getAttribute("aria-label");
        if (!label) return;
        this.hideTooltip();
        this.tooltipAnchor = anchor;
        this.tooltipTarget.textContent = label;
        this.tooltipTarget.hidden = false;
        anchor.setAttribute("aria-describedby", this.tooltipTarget.id);
        const box = anchor.getBoundingClientRect();
        const tooltipBox = this.tooltipTarget.getBoundingClientRect();
        this.tooltipTarget.style.left = `${Math.max(8, Math.min(window.innerWidth - tooltipBox.width - 8, box.left + box.width / 2 - tooltipBox.width / 2))}px`;
        this.tooltipTarget.style.top = `${Math.max(8, box.top - tooltipBox.height - 8)}px`;
    }

    hideTooltip(): void {
        this.tooltipAnchor?.removeAttribute("aria-describedby");
        this.tooltipAnchor = undefined;
        this.tooltipTarget.hidden = true;
    }

    private readonly onItemTooltip = (event: Event): void => this.showTooltip(event);
    private readonly onTooltipLeave = (event: Event): void => {
        if (event instanceof MouseEvent && event.relatedTarget instanceof Node && this.tooltipAnchor?.contains(event.relatedTarget)) return;
        this.hideTooltip();
    };
    private readonly onKeyDown = (event: KeyboardEvent): void => {
        if (event.key === "Escape") this.hideTooltip();
    };

    private readonly onTurboLoad = (): void => {
        this.refreshContext();
    };

    private readonly onTurboRender = (): void => {
        this.refreshContext();
    };

    private readonly onPageShow = (): void => {
        this.refreshContext();
    };

    private readonly onWindowResize = (): void => {
        this.trayDrag?.cancel();
        this.hideTooltip();
        this.cancelTrayResize();
        this.updateTrayHeightVariable();
        this.queueSelectedItemScrollIntoView();
    };

    private readonly onTrayResize = (): void => {
        this.updateTrayHeightVariable();
    };

    private readonly onTourPoiSelectionChanged = (): void => {
        const context = this.currentContext;
        if (context) this.applyTourPoiSelection(getTourPoiSelectionState(document)?.getSnapshot(context.tourContextKey) ?? null);
    };

    private applyTourPoiSelection(snapshot: TourPoiSelectionSnapshot | null): void {
        const context = this.currentContext;
        if (!context || (snapshot && snapshot.tourContextKey !== context.tourContextKey)) return;
        if (snapshot?.poiId && !context.items.some(item => item.id === snapshot.poiId)) {
            getTourPoiSelectionState(document)?.select({ ...snapshot, poiId: null });
            return;
        }
        if (!snapshot?.poiId) {
            this.clearSelectedItem();
            return;
        }
        this.selectItem(snapshot.poiId, { ariaCurrent: snapshot.origin === "page" ? "page" : "location", scrollIntoView: true });
    }

    private readonly onItemClick = (event: MouseEvent): void => {
        if (!document.body.classList.contains("smartguide-map-visible")) {
            return;
        }

        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest<HTMLAnchorElement>("[data-tour-tray-poi-card]");
        if (!link || !this.itemsTarget.contains(link)) {
            return;
        }

        const poiId = link.dataset.poiId?.trim();
        if (!poiId) {
            return;
        }

        event.preventDefault();
        if (this.currentContext) getTourPoiSelectionState(document)?.select({
            tourContextKey: this.currentContext.tourContextKey, poiId, origin: "tray"
        });
    };

    private readonly onBeforeCache = (): void => {
        this.trayDrag?.cancel();
        this.cancelSelectedItemScroll();
        this.cancelTrayResize();
        this.hideTooltip();
        document.body.classList.remove("smartguide-tour-tray-visible", "smartguide-tour-tray-expanded");
        this.clearTrayHeightVariable();
    };

    private connectHeightTracking(): void {
        window.addEventListener("resize", this.onWindowResize);
        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(this.onTrayResize);
            this.resizeObserver.observe(this.element);
        }
    }

    private disconnectHeightTracking(): void {
        window.removeEventListener("resize", this.onWindowResize);
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
    }

    private refreshContext(): void {
        if (document.documentElement.hasAttribute("data-turbo-preview")) return;
        const contextElement = document.querySelector<HTMLScriptElement>("[data-smartguide-tour-tray-context]");
        const text = contextElement?.textContent?.trim();
        if (!text) {
            this.hide();
            return;
        }

        let nextContext: TourTrayContext | undefined;
        try {
            nextContext = normalizeContext(JSON.parse(text) as Record<string, unknown>);
        } catch {
            this.hide();
            return;
        }

        if (!nextContext) {
            this.hide();
            return;
        }

        const state = getTourPoiSelectionState(document);
        const isSameTour = this.currentContext?.tourContextKey === nextContext.tourContextKey;
        const retainedState = this.element.dataset.tourTrayStateScope === nextContext.tourContextKey
            ? this.element.dataset.tourTrayState : undefined;
        const retainedScope = isSameTour || isTrayState(retainedState);
        if (!retainedScope) {
            this.trayDrag?.cancel();
            this.compactTapExpands = false;
            this.cancelSelectedItemScroll();
            this.cancelTrayResize();
            delete this.element.dataset.tourTraySelectedPoi;
        } else if (!isSameTour) {
            this.compactTapExpands = this.element.dataset.tourTrayCompactTap === "expand";
        }
        const canPatchItems = isSameTour && haveSameOrderedItems(this.currentContext?.items, nextContext.items);
        this.currentContext = nextContext;
        state?.activateScope(nextContext.tourContextKey);
        if (nextContext.items.length === 0) {
            if (state?.getSnapshot(nextContext.tourContextKey)?.poiId) {
                state.select({ tourContextKey: nextContext.tourContextKey, poiId: null, origin: "page" });
            }
            this.hide();
            return;
        }
        this.render(canPatchItems);
        state?.seedPage(nextContext.tourContextKey, nextContext.items.find(item => item.isSelected)?.id ?? null, contextElement!, false);
        this.applyTourPoiSelection(state?.getSnapshot(nextContext.tourContextKey) ?? null);
        this.element.hidden = false;
        document.body.classList.add("smartguide-tour-tray-visible");
        this.setTrayState(isSameTour ? this.trayState : isTrayState(retainedState) ? retainedState : "compact");
        if (!retainedScope) this.queueSelectedItemScrollIntoView();
    }

    private render(patchExistingItems = false): void {
        const context = this.currentContext;
        if (!context) {
            return;
        }

        this.titleTarget.textContent = context.tourName;
        if (!patchExistingItems || !this.updateExistingItems(context.items)) {
            this.itemsTarget.replaceChildren(...context.items.map(item => this.createItem(item)));
        }
    }

    private createItem(item: TourTrayPoi): HTMLAnchorElement {
        const link = document.createElement("a");
        link.className = "smartguide-tour-tray__item";
        link.href = item.canonicalUrl;
        link.dataset.tourTrayPoiCard = "true";
        link.dataset.poiId = item.id;
        if (item.isSelected) {
            link.classList.add("smartguide-tour-tray__item--selected");
            link.setAttribute("aria-current", "page");
        }

        const media = document.createElement("span");
        media.className = "smartguide-tour-tray__item-media";
        if (item.imageUrl) {
            const image = document.createElement("img");
            image.src = item.imageUrl;
            image.alt = "";
            image.loading = "lazy";
            image.decoding = "async";
            media.append(image);
        }

        const title = document.createElement("strong");
        title.className = "smartguide-tour-tray__item-title";
        title.textContent = item.name;

        const caption = document.createElement("span");
        caption.className = "smartguide-tour-tray__item-caption";
        caption.append(this.createItemMarker(item), title);

        link.append(media, caption);
        this.updateItemAccessibleName(link, item);
        this.placeItemMarker(link, this.trayState);

        return link;
    }

    private updateExistingItems(items: TourTrayPoi[]): boolean {
        const links = Array.from(this.itemsTarget.querySelectorAll<HTMLAnchorElement>("[data-tour-tray-poi-card]"));
        if (links.length !== items.length) {
            return false;
        }

        for (let index = 0; index < items.length; index += 1) {
            if (links[index].dataset.poiId !== items[index].id) {
                return false;
            }
        }

        links.forEach((link, index) => this.updateItem(link, items[index]));
        return true;
    }

    private updateItem(link: HTMLAnchorElement, item: TourTrayPoi): void {
        link.href = item.canonicalUrl;
        link.dataset.poiId = item.id;
        link.classList.toggle("smartguide-tour-tray__item--selected", item.isSelected === true);
        if (item.isSelected) {
            link.setAttribute("aria-current", "page");
        } else {
            link.removeAttribute("aria-current");
        }

        const media = link.querySelector<HTMLElement>(".smartguide-tour-tray__item-media");
        const image = media?.querySelector<HTMLImageElement>("img");
        if (media && item.imageUrl && !image) {
            const newImage = document.createElement("img");
            newImage.alt = "";
            newImage.loading = "lazy";
            newImage.decoding = "async";
            newImage.src = item.imageUrl;
            media.prepend(newImage);
        } else if (image && item.imageUrl) {
            image.src = item.imageUrl;
        } else if (image) {
            image.remove();
        }

        const marker = link.querySelector<HTMLElement>("[data-tour-tray-marker]");
        const nextMarker = this.createItemMarker(item);
        if (marker) {
            if (marker.dataset.tourTrayMarker !== nextMarker.dataset.tourTrayMarker) {
                marker.className = nextMarker.className;
                marker.dataset.tourTrayMarker = nextMarker.dataset.tourTrayMarker;
                marker.replaceChildren(...Array.from(nextMarker.childNodes));
                if (item.isSubscriberOnly) marker.dataset.tourTrayPoiLocked = "true";
                else delete marker.dataset.tourTrayPoiLocked;
            } else if (!item.isSubscriberOnly) {
                marker.textContent = String(item.order);
            }
        } else {
            link.querySelector<HTMLElement>(".smartguide-tour-tray__item-caption")?.prepend(nextMarker);
        }

        const title = link.querySelector<HTMLElement>(".smartguide-tour-tray__item-title");
        if (title) {
            title.textContent = item.name;
        }
        this.updateItemAccessibleName(link, item);
        this.placeItemMarker(link, this.trayState);
    }

    private updateItemAccessibleName(link: HTMLAnchorElement, item: TourTrayPoi): void {
        link.setAttribute("aria-label", item.isSubscriberOnly
            ? formatTranslation(webTranslations.lockedTourPoiAccessibleNameFormat, String(item.order), item.name, webTranslations.lockedPremiumTourPlace)
            : formatTranslation(webTranslations.tourPoiAccessibleNameFormat, String(item.order), item.name));
    }

    private placeItemMarker(link: HTMLAnchorElement, state: TourTrayState): void {
        const marker = link.querySelector<HTMLElement>("[data-tour-tray-marker]");
        const parent = link.querySelector<HTMLElement>(state === "compact" ? ".smartguide-tour-tray__item-media" : ".smartguide-tour-tray__item-caption");
        if (marker && parent && marker.parentElement !== parent) parent.prepend(marker);
    }

    private createItemMarker(item: TourTrayPoi): HTMLSpanElement {
        const marker = document.createElement("span");
        marker.setAttribute("aria-hidden", "true");
        marker.dataset.tourTrayMarker = item.isSubscriberOnly ? "lock" : "number";
        if (item.isSubscriberOnly === true) {
            marker.className = "smartguide-tour-tray__item-lock";
            marker.dataset.tourTrayPoiLocked = "true";
            marker.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2Zm-7 0V7a2 2 0 1 1 4 0v2h-4Zm3 7.7V18h-2v-1.3a2 2 0 1 1 2 0Z" /></svg>';
            return marker;
        }

        marker.className = "smartguide-tour-tray__item-order";
        marker.textContent = String(item.order);
        return marker;
    }

    private selectItem(
        poiId: string,
        options: { ariaCurrent: "page" | "location"; scrollIntoView: boolean }
    ): void {
        const context = this.currentContext;
        if (!context?.items.some(item => item.id === poiId)) {
            return;
        }
        const changed = this.element.dataset.tourTraySelectedPoi !== poiId;
        this.element.dataset.tourTraySelectedPoi = poiId;

        this.currentContext = {
            ...context,
            items: context.items.map(item => ({
                ...item,
                isSelected: item.id === poiId
            }))
        };

        this.itemsTarget.querySelectorAll<HTMLAnchorElement>("[data-tour-tray-poi-card]").forEach(link => {
            const isSelected = link.dataset.poiId === poiId;
            link.classList.toggle("smartguide-tour-tray__item--selected", isSelected);
            if (isSelected) {
                link.setAttribute("aria-current", options.ariaCurrent);
            } else {
                link.removeAttribute("aria-current");
            }
        });

        if (options.scrollIntoView && changed) {
            this.queueSelectedItemScrollIntoView();
        }
    }

    private clearSelectedItem(): void {
        this.cancelSelectedItemScroll();
        delete this.element.dataset.tourTraySelectedPoi;
        const context = this.currentContext;
        if (!context?.items.some(item => item.isSelected === true)) {
            return;
        }

        this.currentContext = {
            ...context,
            items: context.items.map(item => ({
                ...item,
                isSelected: false
            }))
        };

        this.itemsTarget.querySelectorAll<HTMLAnchorElement>("[data-tour-tray-poi-card]").forEach(link => {
            link.classList.remove("smartguide-tour-tray__item--selected");
            link.removeAttribute("aria-current");
        });
    }

    private queueSelectedItemScrollIntoView(): void {
        if (this.element.hidden || this.trayState === "collapsed" || !this.currentContext) return;
        if (this.resizeAnimationFrame !== undefined || this.resizeCompletionTimer !== undefined) {
            this.resizeWantsAlignment = true;
            return;
        }
        if (typeof requestAnimationFrame !== "function") {
            this.scrollSelectedItemIntoView();
            return;
        }

        if (this.selectedItemScrollAnimationFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.selectedItemScrollAnimationFrame);
        }

        this.selectedItemScrollAnimationFrame = requestAnimationFrame(() => {
            this.selectedItemScrollAnimationFrame = undefined;
            this.scrollSelectedItemIntoView();
        });
    }

    private scrollSelectedItemIntoView(): void {
        if (this.element.hidden || this.trayState === "collapsed" || this.resizeAnimationFrame !== undefined || this.resizeCompletionTimer !== undefined) return;
        const selectedLink = this.itemsTarget.querySelector<HTMLAnchorElement>(".smartguide-tour-tray__item--selected");
        if (!selectedLink) {
            return;
        }

        this.scrollRailToSelectedItem(selectedLink);
    }

    private scrollRailToSelectedItem(selectedLink: HTMLAnchorElement): void {
        const rail = this.bodyTarget;
        const railBox = rail.getBoundingClientRect();
        const selectedBox = selectedLink.getBoundingClientRect();
        if (railBox.width <= 0 || selectedBox.width <= 0) {
            return;
        }

        const itemStyles = getComputedStyle(this.itemsTarget);
        const rtl = getComputedStyle(rail).direction === "rtl";
        const sign = rtl ? -1 : 1;
        const selectedLeft = rtl
            ? railBox.right - selectedBox.right - rail.scrollLeft
            : selectedBox.left - railBox.left + rail.scrollLeft;
        const thumbnailGap = cssPixelValue(itemStyles.columnGap || itemStyles.gap);
        const startInset = Math.max(0, thumbnailGap);
        const nextScrollLeft = sign * Math.max(0, Math.min(Math.max(0, rail.scrollWidth - rail.clientWidth), selectedLeft - startInset));
        if (this.scrollTarget !== undefined && Math.abs(nextScrollLeft - this.scrollTarget) < 1) {
            return;
        }
        this.cancelScrollAnimation();
        this.horizontalRailScroller?.stopMomentum();
        if (Math.abs(nextScrollLeft - rail.scrollLeft) < 1) {
            return;
        }

        if (this.reducedMotion() || typeof requestAnimationFrame !== "function") {
            rail.scrollLeft = nextScrollLeft;
            return;
        }

        const startScrollLeft = rail.scrollLeft;
        let startedAt: number | undefined;
        this.scrollTarget = nextScrollLeft;
        const animate = (timestamp: number): void => {
            startedAt ??= timestamp;
            const progress = Math.min(1, (timestamp - startedAt) / 320);
            const easedProgress = 1 - Math.pow(1 - progress, 3);
            rail.scrollLeft = startScrollLeft + (nextScrollLeft - startScrollLeft) * easedProgress;
            if (progress < 1) {
                this.scrollAnimationFrame = requestAnimationFrame(animate);
            } else {
                this.scrollAnimationFrame = undefined;
                this.scrollTarget = undefined;
            }
        };
        this.scrollAnimationFrame = requestAnimationFrame(animate);
    }

    private readonly cancelSelectedItemScroll = (): void => {
        this.hideTooltip();
        this.resizeWantsAlignment = false;
        if (this.selectedItemScrollAnimationFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.selectedItemScrollAnimationFrame);
            this.selectedItemScrollAnimationFrame = undefined;
        }
        this.cancelScrollAnimation();
    };

    private cancelScrollAnimation(): void {
        if (this.scrollAnimationFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.scrollAnimationFrame);
        }
        this.scrollAnimationFrame = undefined;
        this.scrollTarget = undefined;
    }

    private setTrayState(nextState: TourTrayState, userInitiated = false, dragHeight?: number): void {
        const focused = document.activeElement;
        const changed = this.trayState !== nextState;
        const previousHeight = dragHeight ?? this.element.getBoundingClientRect().height;
        if (changed) {
            this.cancelSelectedItemScroll();
            this.cancelTrayResize();
            this.resizeWantsAlignment = nextState !== "collapsed";
        }
        this.trayState = nextState;
        if (nextState !== "compact") this.compactTapExpands = nextState === "collapsed";
        this.element.dataset.tourTrayCompactTap = this.compactTapExpands ? "expand" : "collapse";
        this.applyTrayAppearance(nextState);
        if (this.currentContext) this.element.dataset.tourTrayStateScope = this.currentContext.tourContextKey;
        const expanded = nextState !== "collapsed";
        this.expandButtonTarget.hidden = nextState === "full";
        this.collapseButtonTarget.hidden = nextState === "collapsed";
        this.expandButtonTarget.setAttribute("aria-expanded", String(expanded));
        this.collapseButtonTarget.setAttribute("aria-expanded", String(expanded));
        const expandLabel = nextState === "collapsed" ? webTranslations.showCompactTourTray : webTranslations.expandFullTourTray;
        const collapseLabel = nextState === "full" ? webTranslations.showCompactTourTray : webTranslations.collapseTourTray;
        this.expandButtonTarget.setAttribute("aria-label", expandLabel);
        this.collapseButtonTarget.setAttribute("aria-label", collapseLabel);
        this.toggleButtonTarget.setAttribute("aria-expanded", String(expanded));
        const toggleLabel = nextState === "compact"
            ? this.compactTapExpands ? expandLabel : collapseLabel
            : webTranslations.showCompactTourTray;
        this.toggleButtonTarget.setAttribute("aria-label", [this.currentContext?.tourName, toggleLabel].filter(Boolean).join(": "));
        if (userInitiated && focused === this.expandButtonTarget && this.expandButtonTarget.hidden) {
            this.collapseButtonTarget.focus();
        } else if (userInitiated && focused === this.collapseButtonTarget && this.collapseButtonTarget.hidden) {
            this.expandButtonTarget.focus();
        }
        this.hideTooltip();
        document.body.classList.toggle("smartguide-tour-tray-expanded", expanded);
        this.updateTrayHeightVariable();
        if (userInitiated && (changed || dragHeight !== undefined)) this.animateTrayResize(previousHeight);
        if (expanded && changed && !userInitiated) {
            this.queueSelectedItemScrollIntoView();
        } else if (!expanded) {
            this.cancelSelectedItemScroll();
        }
    }

    private animateTrayResize(previousHeight: number): void {
        const destinationHeight = this.element.getBoundingClientRect().height;
        if (this.reducedMotion() || previousHeight <= 0 || destinationHeight <= 0 || previousHeight === destinationHeight) {
            this.finishTrayResize();
            return;
        }
        this.element.style.height = `${previousHeight}px`;
        this.element.getBoundingClientRect();
        const start = (): void => {
            this.resizeAnimationFrame = undefined;
            this.element.style.transition = "height 200ms ease";
            this.element.style.height = `${destinationHeight}px`;
            this.resizeCompletionTimer = setTimeout(() => this.finishTrayResize(), 250);
        };
        if (typeof requestAnimationFrame === "function") this.resizeAnimationFrame = requestAnimationFrame(start);
        else start();
    }

    private readonly onResizeTransitionEnd = (event: TransitionEvent): void => {
        if (event.target === this.element && event.propertyName === "height") this.finishTrayResize();
    };

    private finishTrayResize(): void {
        const align = this.resizeWantsAlignment;
        this.resizeWantsAlignment = false;
        this.cancelTrayResize();
        this.updateTrayHeightVariable();
        if (align) this.queueSelectedItemScrollIntoView();
    }

    private cancelTrayResize(): void {
        if (this.resizeAnimationFrame !== undefined) cancelAnimationFrame(this.resizeAnimationFrame);
        this.resizeAnimationFrame = undefined;
        if (this.resizeCompletionTimer !== undefined) clearTimeout(this.resizeCompletionTimer);
        this.resizeCompletionTimer = undefined;
        this.element.style.removeProperty("transition");
        this.element.style.removeProperty("height");
    }

    private reducedMotion(): boolean {
        return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    }

    private hide(): void {
        this.trayDrag?.cancel();
        this.cancelSelectedItemScroll();
        this.cancelTrayResize();
        this.currentContext = undefined;
        this.element.hidden = true;
        this.itemsTarget.replaceChildren();
        document.body.classList.remove("smartguide-tour-tray-visible", "smartguide-tour-tray-expanded");
        this.setTrayState("collapsed");
        delete this.element.dataset.tourTrayStateScope;
        delete this.element.dataset.tourTrayState;
        delete this.element.dataset.tourTraySelectedPoi;
        delete this.element.dataset.tourTrayCompactTap;
        this.clearTrayHeightVariable();
    }

    private updateTrayHeightVariable(): void {
        if (this.element.hidden) {
            this.clearTrayHeightVariable();
            return;
        }

        const height = Number(this.element.getBoundingClientRect().height);
        if (!Number.isFinite(height) || height <= 0) {
            this.clearTrayHeightVariable();
            return;
        }

        document.body.style.setProperty("--smartguide-tour-tray-height", `${Math.round(height)}px`);
    }

    private clearTrayHeightVariable(): void {
        document.body.style.removeProperty("--smartguide-tour-tray-height");
    }
}

function isTrayState(value: string | undefined): value is TourTrayState {
    return value === "collapsed" || value === "compact" || value === "full";
}

function normalizeContext(value: Record<string, unknown>): TourTrayContext | undefined {
    const tourContextKey = stringValue(read(value, "tourContextKey")).trim();
    const itemsValue = read(value, "items");
    if (!tourContextKey || !Array.isArray(itemsValue)) {
        return undefined;
    }

    const items = itemsValue
        .map(item => normalizeItem(item as Record<string, unknown>))
        .filter((item): item is TourTrayPoi => item !== undefined);

    return {
        tourContextKey,
        tourId: stringValue(read(value, "tourId")),
        tourName: stringValue(read(value, "tourName")),
        tourUrl: stringValue(read(value, "tourUrl")),
        tourType: stringValue(read(value, "tourType")),
        visitedCount: numberValue(read(value, "visitedCount")),
        items,
        expandedOnLoad: read(value, "expandedOnLoad") === true
    };
}

function normalizeItem(value: Record<string, unknown>): TourTrayPoi | undefined {
    const id = stringValue(read(value, "id"));
    const name = stringValue(read(value, "name"));
    const canonicalUrl = stringValue(read(value, "canonicalUrl"));
    const order = numberValue(read(value, "order"));
    if (!id || !name || !canonicalUrl || order <= 0) {
        return undefined;
    }

    return {
        id,
        name,
        imageUrl: stringValue(read(value, "imageUrl")) || null,
        order,
        canonicalUrl,
        isSelected: read(value, "isSelected") === true,
        isSubscriberOnly: read(value, "isSubscriberOnly") === true
    };
}

function haveSameOrderedItems(previousItems: TourTrayPoi[] | undefined, nextItems: TourTrayPoi[]): boolean {
    if (!previousItems || previousItems.length !== nextItems.length) {
        return false;
    }

    return previousItems.every((item, index) => item.id === nextItems[index].id);
}

function read(value: Record<string, unknown>, camelName: string): unknown {
    const pascalName = `${camelName[0]?.toUpperCase() ?? ""}${camelName.slice(1)}`;
    return value[camelName] ?? value[pascalName];
}

function stringValue(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function numberValue(value: unknown): number {
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function cssPixelValue(value: string): number {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
}
